import Card from '@pinecast/common/Card';
import Checkbox from '@pinecast/common/Checkbox';
import {fromNow} from '@pinecast/common/dates';
import EmptyState from '@pinecast/common/EmptyState';
import ErrorState from '@pinecast/common/ErrorState';
import Group, {VerticalGroup} from '@pinecast/common/Group';
import SearchItems from '@pinecast/common/icons/SearchItems';
import Link from '@pinecast/common/Link';
import LoadingState from '@pinecast/common/LoadingState';
import Section from '@pinecast/common/Section';
import Tag from '@pinecast/common/Tag';
import {DashboardTitle, HelpText, P} from '@pinecast/common/Text';
import TextInput from '@pinecast/common/TextInput';
import ReadOnlyLinkInput from '@pinecast/db-spa/ReadOnlyLinkInput';
import {useQuery} from '@pinecast/gql';
import type {RecurringDonation_tipJarSubscription} from '@pinecast/gql/__generated_tj__/RecurringDonation';
import type {
  SubscriptionEpisodes,
  SubscriptionEpisodesVariables,
} from '@pinecast/gql/__generated_tj__/SubscriptionEpisodes';
import {defineMessages, FormattedMessage, useIntl} from '@pinecast/i18n';
import {useColors, useCSS} from '@pinecast/styles';
import {test} from 'fuzzyjs';
import gql from 'graphql-tag';
import * as React from 'react';

const messages = defineMessages({
  ineligible: {
    id: 'tj-spa.RecurringDonationPagePrivateContent.ineligible',
    description:
      'Header for when the subscriber is ineligible for private content',
    defaultMessage: 'Your subscription does not include private content',
  },
  ineligibleCopy: {
    id: 'tj-spa.RecurringDonationPagePrivateContent.ineligible.copy',
    description:
      'Copy for when the subscriber is ineligible for private content',
    defaultMessage:
      'This podcast requires a minimum subscription amount to access premium content. You can listen to public episodes, or <upgradeLink>upgrade your subscription</upgradeLink>.',
  },
  subscribe: {
    id: 'tj-spa.RecurringDonationPagePrivateContent.subscribe',
    description: 'Header for private content access on a podcast subscription',
    defaultMessage: 'Subscribe in an app',
  },
  privateCopy: {
    id: 'tj-spa.RecurringDonationPagePrivateContent.privateCopy',
    description:
      'Copy for private content access on a podcast subscription. {podcast} is the name of the show.',
    defaultMessage:
      // "You're eligible to access the private episodes for {podcast}. Subscribe to your unique private feed URL, or listen here in your browser.",
      "You're eligible to access the private episodes for {podcast}. Subscribe to your unique private feed URL to listen.",
  },
  privateHelp: {
    id: 'tj-spa.RecurringDonationPagePrivateContent.privateHelp',
    description:
      'Help text for private content access on a podcast subscription.',
    defaultMessage: 'Need help subscribing to your private feed URL?',
  },

  loadingEpisodes: {
    id: 'tj-spa.RecurringDonationPagePrivateContent.loadingEpisodes',
    description: 'Loading text for podcast episodes',
    defaultMessage: 'Loading episodes…',
  },
  errorEpisodes: {
    id: 'tj-spa.RecurringDonationPagePrivateContent.errorEpisodes',
    description: 'Error text for podcast episodes',
    defaultMessage: 'There was a problem loading the episode list.',
  },

  listen: {
    id: 'tj-spa.RecurringDonationPagePrivateContent.listen',
    description: 'Header for private content access on a podcast subscription',
    defaultMessage: 'Listen in your browser',
  },
  filterText: {
    id: 'tj-spa.RecurringDonationPagePrivateContent.filterText',
    description: 'Label for episodes filter',
    defaultMessage: 'Filter episodes',
  },
  filterPrivate: {
    id: 'tj-spa.RecurringDonationPagePrivateContent.filterPrivate',
    description: 'Label for checkbox to only show private episodes',
    defaultMessage: 'Only show premium content',
  },

  episodePremium: {
    id: 'tj-spa.RecurringDonationPagePrivateContent.episode.premium',
    description: 'Label for episodes that are exclusive to paid listeners',
    defaultMessage: 'Premium',
  },
  episodeUnlistened: {
    id: 'tj-spa.RecurringDonationPagePrivateContent.episode.unlistened',
    description: 'Label for episodes that have not been listened to',
    defaultMessage: 'Not listened to yet',
  },
  episodeListened: {
    id: 'tj-spa.RecurringDonationPagePrivateContent.episode.listened',
    description:
      'Label for episodes that have been listened to. {when} is a relative date, like "last week"',
    defaultMessage: 'Listened {when}',
  },
  episodePublished: {
    id: 'tj-spa.RecurringDonationPagePrivateContent.episode.published',
    description:
      'Label for when episodes were published. {when} is a relative date, like "last week"',
    defaultMessage: 'Published {when}',
  },

  emptyFilter: {
    id: 'tj-spa.RecurringDonationPagePrivateContent.emptyFilter',
    description: 'Message when all episodes have been filtered out',
    defaultMessage: 'No episodes match your filter.',
  },
  emptyFilterCopy: {
    id: 'tj-spa.RecurringDonationPagePrivateContent.emptyFilter.copy',
    description: 'Copy when all episodes have been filtered out',
    defaultMessage:
      'Try changing your search, or consider starting your own podcast about that topic.',
  },
});

const EPISODES = gql`
  query SubscriptionEpisodes($slug: ID!) {
    tipJarEpisodeInstances(podcastSlug: $slug) {
      episode {
        id
        title
        publish
      }
      isPrivate
      listened
      url
    }
  }
`;

type Props = {
  subscription: RecurringDonation_tipJarSubscription;
};

const RecurringDonationPagePrivateContent = ({subscription}: Props) => {
  const designColors = useColors();
  const css = useCSS();
  const intl = useIntl();

  const episodes = useQuery<
    SubscriptionEpisodes,
    SubscriptionEpisodesVariables
  >(EPISODES, {
    variables: {slug: subscription.podcast.slug},
  });

  const [filter, setFilter] = React.useState('');
  const [filterPrivate, setFilterPrivate] = React.useState(
    subscription.eligibleForPrivateContent,
  );

  const wrapper = (content: React.ReactNode) => (
    <>
      {subscription.eligibleForPrivateContent ? (
        <Section>
          <DashboardTitle>
            <FormattedMessage {...messages.subscribe} />
          </DashboardTitle>
          <P>
            <FormattedMessage
              {...messages.privateCopy}
              values={{podcast: subscription.podcast.name}}
            />
          </P>
          <ReadOnlyLinkInput
            disableVisit
            value={`https://pinecast.com${subscription.privateFeedUrl}`}
          />
          <HelpText style={{marginTop: 4}}>
            <Link href="https://help.pinecast.com/article/61-subscribing-to-a-private-rss-feed">
              <FormattedMessage {...messages.privateHelp} />
            </Link>
          </HelpText>
        </Section>
      ) : (
        <Section>
          <EmptyState
            title={<FormattedMessage {...messages.ineligible} />}
            copy={
              <FormattedMessage
                {...messages.ineligibleCopy}
                values={{
                  upgradeLink: (...a) => (
                    <Link
                      forceHardNavigation
                      href={`/jar/${subscription.podcast.slug}`}
                    >
                      {a}
                    </Link>
                  ),
                }}
              />
            }
          />
        </Section>
      )}
      {content}
    </>
  );

  if (episodes.loading) {
    return wrapper(
      <Section>
        <LoadingState
          title={<FormattedMessage {...messages.loadingEpisodes} />}
        />
      </Section>,
    );
  }
  if (episodes.error || !episodes.data.tipJarEpisodeInstances) {
    return wrapper(
      <Section>
        <ErrorState title={<FormattedMessage {...messages.errorEpisodes} />} />
      </Section>,
    );
  }

  const filteredEpisodeInstances = episodes.data.tipJarEpisodeInstances
    .filter(instance => !filterPrivate || instance.isPrivate)
    .filter(instance => !filter || test(filter, instance.episode.title));

  return wrapper(
    <Section>
      <DashboardTitle>
        <FormattedMessage {...messages.listen} />
      </DashboardTitle>
      <Card style={{padding: 12}}>
        <Group spacing={16} allowWrap centerChildren>
          <TextInput
            aria-label={intl.formatMessage(messages.filterText)}
            placeholder={intl.formatMessage(messages.filterText)}
            onChange={setFilter}
            value={filter}
          />
          {subscription.eligibleForPrivateContent && (
            <Checkbox
              checked={filterPrivate}
              onChange={setFilterPrivate}
              style={{paddingBottom: 0}}
              text={<FormattedMessage {...messages.filterPrivate} />}
            />
          )}
        </Group>
      </Card>
      {filteredEpisodeInstances.length ? (
        filteredEpisodeInstances.map(instance => (
          <Link
            key={instance.episode.id}
            href={`/subscriptions/recurring_tips/${subscription.podcast.slug}/listen/${instance.episode.id}`}
            style={{
              display: 'block',
              textDecoration: 'none',
              marginBottom: '24px',
            }}
          >
            <VerticalGroup spacing={4}>
              <Group spacing={4} centerChildren>
                {instance.isPrivate && (
                  <Tag color="blue">
                    <FormattedMessage {...messages.episodePremium} />
                  </Tag>
                )}
                <b className={css({fontSize: '16px'})}>
                  {instance.episode.title}
                </b>
              </Group>
              <Group
                allowWrap
                spacing={16}
                wrapperStyle={{color: designColors.contentTextAccent}}
              >
                {!instance.listened ? (
                  <span>
                    <FormattedMessage {...messages.episodeUnlistened} />
                  </span>
                ) : (
                  <Tag color="gray" style={{alignSelf: 'flex-start'}}>
                    <time dateTime={instance.listened}>
                      <FormattedMessage
                        {...messages.episodeListened}
                        values={{
                          when: fromNow(instance.listened),
                        }}
                      />
                    </time>
                  </Tag>
                )}

                <time dateTime={instance.episode.publish}>
                  <FormattedMessage
                    {...messages.episodePublished}
                    values={{
                      when: fromNow(instance.episode.publish),
                    }}
                  />
                </time>
              </Group>
            </VerticalGroup>
          </Link>
        ))
      ) : (
        <EmptyState
          title={<FormattedMessage {...messages.emptyFilter} />}
          copy={<FormattedMessage {...messages.emptyFilterCopy} />}
          icon={SearchItems}
        />
      )}
    </Section>,
  );
};

export default RecurringDonationPagePrivateContent;
